/**
 * The facts every Work Home surface ranks against, and the surfaces themselves.
 * One context, so a new signal is a field here rather than a prop chain through
 * the scene. This is the shape a backend recommender takes over when ranking
 * moves server-side.
 */
import type { WorkHomeRankingPolicy } from '../../../contracts';
import type { WorkHomeTaskRow } from '../../../data';

export type WorkHomeSurface = 'composer' | 'sessions' | 'tasks' | 'patients' | 'library';

/** A session a row can name and attach itself to. */
export interface WorkHomeSessionRef {
  sessionId: string;
  name?: string;
  isToday: boolean;
  when: string;
}

/** What the surface is pointed at. */
export interface WorkHomeFocus {
  kind: 'visit' | 'patient' | 'none';
  sessionId?: string;
  patientId?: string;
}

export const WORK_HOME_UNFOCUSED: WorkHomeFocus = { kind: 'none' };

export interface WorkContext {
  focus?: WorkHomeFocus;
  /** Open clinical tasks, already narrowed to `focus`. Suggested work keeps waiting ones. */
  leftover?: readonly WorkHomeTaskRow[];
  /** Newest dated visit, so a row can name the session it attaches to. */
  targetSession?: WorkHomeSessionRef;
  visitHasPatient?: boolean;
  ehr?: {
    connected?: boolean;
    pending?: boolean;
    /** False when the connected EHR exposes no schedule Heidi can read. */
    scheduleReadable?: boolean;
  };
  patients?: { hasAny?: boolean; pending?: boolean };
  sessions?: {
    hasAny?: boolean;
    pending?: boolean;
    /** Untitled visit worth continuing instead of explaining recording again. */
    newestUnfinished?: WorkHomeSessionRef;
  };
  tasks?: { pending?: boolean };
  library?: { pending?: boolean };
  /** Work chats parked on a question or approval — float those tasks. */
  waitingWorkProjectIds?: ReadonlySet<string>;
  /** Ordering arm for the idle composer. Defaults to the tiered heuristic. */
  rankingPolicy?: WorkHomeRankingPolicy;
  /** Fixed per scene mount so a shuffled shelf holds still across re-renders. */
  shuffleSeed?: number;
  /**
   * Whether the account can reach the workflow catalog. The idle composer's
   * routine starter opens that same privileged surface, so it is omitted
   * when this is not `true` (app-resolved flag, crossed in as a field here).
   */
  workflowsEnabled?: boolean;
  /**
   * Resolved app-side from `IS_NON_US_SUGGESTIONS_COMPLIANCE_ENABLED() &&
   * !isUSMarket()` (see
   * docs/design-docs/compliance/01-homepage-suggestions-non-us-gating.md).
   * The package cannot read the flag or the market itself, so the app
   * crosses in the already-resolved boolean. When `true`, the idle
   * composer's treatment-plan row swaps to its retrieval-only, non-advisory
   * copy for Tier 2/3 markets.
   */
  nonUsTreatmentPlanVariant?: boolean;
}

export const workHomeFocus = (context: WorkContext): WorkHomeFocus =>
  context.focus ?? WORK_HOME_UNFOCUSED;
